import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldStar, ArrowLeft, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { PasswordField } from "@/pages/Login";

function EmailStep({ email, setEmail, onSent }) {
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data.message);
      onSent();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="forgot-email-form">
      <div>
        <Label htmlFor="reset-email">Email</Label>
        <Input id="reset-email" type="email" required data-testid="reset-email-input" className="mt-1.5 bg-secondary/50"
          value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@department.ca" />
      </div>
      <Button type="submit" disabled={loading} data-testid="send-reset-code-button" className="w-full rounded-md font-semibold">
        {loading ? "Sending…" : "Email me a reset code"}
      </Button>
    </form>
  );
}

function CodeStep({ email, onResend }) {
  const navigate = useNavigate();
  const [form, setForm] = useState({ code: "", password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const mismatch = form.confirm.length > 0 && form.password !== form.confirm;
  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords don't match. Please re-enter them.");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { email, code: form.code, new_password: form.password });
      toast.success(data.message);
      navigate("/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="reset-code-form">
      <div className="flex gap-2 rounded-md border border-border bg-secondary/40 p-3 text-sm text-muted-foreground" data-testid="reset-code-sent-note">
        <EnvelopeSimple className="h-5 w-5 shrink-0 text-primary" />
        <p>If an account exists for <strong className="text-foreground">{email}</strong>, a 6-digit code is on its way. Check your spam folder too.</p>
      </div>
      <div>
        <Label htmlFor="reset-code">Reset code</Label>
        <Input id="reset-code" required inputMode="numeric" autoComplete="one-time-code" maxLength={6} data-testid="reset-code-input"
          className="mt-1.5 bg-secondary/50 tracking-[0.5em] text-center text-lg font-bold" placeholder="000000"
          value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.replace(/\D/g, "") })} />
      </div>
      <PasswordField id="new-password" label="New password" testId="new-password-input" value={form.password}
        onChange={(e) => setForm({ ...form, password: e.target.value })} />
      <div>
        <PasswordField id="confirm-new-password" label="Confirm new password" testId="confirm-new-password-input" invalid={mismatch}
          value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} />
        {mismatch && <p className="text-xs text-destructive mt-1.5" role="alert" data-testid="reset-password-mismatch-error">Passwords don't match.</p>}
      </div>
      <p className="text-xs text-muted-foreground">At least 6 characters.</p>
      <Button type="submit" disabled={loading || mismatch || form.code.length !== 6} data-testid="reset-password-submit-button" className="w-full rounded-md font-semibold">
        {loading ? "Updating…" : "Reset password"}
      </Button>
      <button type="button" onClick={onResend} data-testid="resend-code-button" className="w-full text-sm text-primary font-semibold hover:underline">
        Didn't get it? Send a new code
      </button>
    </form>
  );
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  return (
    <div className="min-h-screen flex items-center justify-center p-6 sm:p-12" data-testid="forgot-password-page">
      <div className="w-full max-w-sm animate-fade-up">
        <div className="flex items-center gap-2 mb-8">
          <ShieldStar className="h-7 w-7 text-primary" weight="fill" />
          <span className="text-xl font-black tracking-tight">MyPayAlert</span>
        </div>
        <h1 className="text-3xl font-black tracking-tight">{sent ? "Enter your code" : "Forgot password?"}</h1>
        <p className="text-muted-foreground mt-2 mb-8 text-sm">
          {sent ? "Enter the code from your email and choose a new password." : "Enter your account email and we'll send you a secure 6-digit reset code."}
        </p>
        {sent ? <CodeStep email={email} onResend={() => setSent(false)} /> : <EmailStep email={email} setEmail={setEmail} onSent={() => setSent(true)} />}
        <Link to="/login" data-testid="back-to-sign-in-link" className="mt-6 flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground">
          <ArrowLeft className="h-4 w-4" /> Back to sign in
        </Link>
      </div>
    </div>
  );
}
